import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { PdfViewerProps, PdfjsField } from './PdfViewer.model'
import { base64ToBlobUrl } from '../PdfViewerLegacy/pdf.utils'
import WebViewer from '@pdftron/webviewer'
import styles from './PdfViewer.module.scss'
import classNames from 'classnames'
import { PDFJS_EXPRESS_KEY, PDFJS_EXPRESS_PATH } from '../../../../environment/pdfjsExpress'

const CUSTOM_EVENT_FIELD_CHANGED = 'CUSTOM_EVENT_FIELD_CHANGED'
const CUSTOM_EVENT_TEXT_WIDGET_FOCUS = 'CUSTOM_EVENT_TEXT_WIDGET_FOCUS'
const CUSTOM_EVENT_TEXT_WIDGET_DBCLICK = 'CUSTOM_EVENT_TEXT_WIDGET_DBCLICK'

export const PdfViewer: FC<PdfViewerProps> = ({
  fileContent,
  fileUrl,
  fileName,
  fieldValues,
  readOnly,
  onExitFullScreen,
  onEnterFullScreen,
  onDbClick,
  onChange,
  onFocusInput,
}) => {
  const isFirstRender = useRef(true)
  const [webViewerInstance, setWebViewerInstance] = useState<null | any>(null)
  const [isAnnotationsLoaded, setAnnotationsLoaded] = useState(false)
  const viewer = useRef(null)
  const readOnlyRef = useRef(readOnly)

  useEffect(() => {
    readOnlyRef.current = readOnly
  }, [readOnly])

  const handleDbClick = useCallback(() => {
    onDbClick && !readOnly && onDbClick()
  }, [onDbClick, readOnly])

  const handleFocus = useCallback(
    (event: CustomEvent) => {
      const oldFieldMatch = event.detail.name?.match(/#(.*)#$/)

      onFocusInput &&
        onFocusInput({
          id: oldFieldMatch && oldFieldMatch[1] ? oldFieldMatch[1] : event.detail.name,
          name: event.detail.name,
        })
    },
    [onFocusInput],
  )

  const handleFieldChanged = useCallback(
    (event: CustomEvent) => {
      if (onChange && !readOnly) {
        const oldFieldMatch = event.detail.field.name?.match(/#(.*)#$/)
        const fieldValue = {
          id: oldFieldMatch && oldFieldMatch[1] ? oldFieldMatch[1] : event.detail.field.name,
          name: event.detail.field.name,
          value: event.detail.value,
          type: 'value',
        }
        onChange(fieldValue)
      }
    },
    [onChange, readOnly],
  )

  const onToggleFullscreen = useCallback(
    (e) => {
      if (e.detail.isInFullscreen) {
        webViewerInstance.UI.setFitMode(webViewerInstance.UI.FitMode.FitPage)
        onEnterFullScreen && onEnterFullScreen()
      } else {
        webViewerInstance.UI.setFitMode(webViewerInstance.UI.FitMode.FitWidth)
      }
    },
    [onEnterFullScreen, webViewerInstance],
  )

  const getFileUrl = useCallback(() => {
    if (fileContent) {
      return base64ToBlobUrl(fileContent)
    }

    if (fileUrl) {
      return fileUrl
    }

    throw new Error('You must provide "fileContent" or "fileUrl"')
  }, [fileContent, fileUrl])

  const handleExitFullScreen = useCallback(() => {
    if (!document.fullscreenElement && onExitFullScreen) onExitFullScreen()
  }, [onExitFullScreen])

  useEffect(() => {
    document.body.addEventListener(CUSTOM_EVENT_TEXT_WIDGET_FOCUS, handleFocus as EventListener)
    return () =>
      document.body.removeEventListener(
        CUSTOM_EVENT_TEXT_WIDGET_FOCUS,
        handleFocus as EventListener,
      )
  }, [handleFocus])

  useEffect(() => {
    document.body.addEventListener(CUSTOM_EVENT_TEXT_WIDGET_DBCLICK, handleDbClick as EventListener)
    return () =>
      document.body.removeEventListener(
        CUSTOM_EVENT_TEXT_WIDGET_DBCLICK,
        handleDbClick as EventListener,
      )
  }, [handleDbClick])

  useEffect(() => {
    document.body.addEventListener(CUSTOM_EVENT_FIELD_CHANGED, handleFieldChanged as EventListener)
    return () =>
      document.body.removeEventListener(
        CUSTOM_EVENT_FIELD_CHANGED,
        handleFieldChanged as EventListener,
      )
  }, [handleFieldChanged])

  useEffect(() => {
    if (webViewerInstance) {
      webViewerInstance.UI.addEventListener(
        webViewerInstance.UI.Events.FULLSCREEN_MODE_TOGGLED,
        onToggleFullscreen,
      )
      return () =>
        webViewerInstance.UI.removeEventListener(
          webViewerInstance.UI.Events.FULLSCREEN_MODE_TOGGLED,
          onToggleFullscreen,
        )
    }
  }, [onToggleFullscreen, webViewerInstance])

  const onInitWebViewer = (instance) => {
    const { UI, Core } = instance
    const { Annotations, documentViewer, annotationManager } = Core

    UI.disableFeatures([UI.Feature.Print])
    UI.setLayoutMode(UI.LayoutMode.Continuous)
    UI.setLanguage('fr')
    documentViewer.addEventListener('documentLoaded', () => {
      UI.setFitMode(UI.FitMode.FitWidth)
     // UI.setZoomLevel('111%')
      setAnnotationsLoaded(false)
    })


    Annotations.WidgetAnnotation.getCustomStyles = (widget) => {
      const isReadOnly = readOnlyRef.current
      const commonStyle = {
        color: 'rgb(47, 47, 47)',
        'font-family': 'sans-serif',
        'pointer-events': isReadOnly ? 'none' : 'auto',
      }
      if (widget instanceof Annotations.TextWidgetAnnotation) {
        return {
          ...commonStyle,
          'background-color': isReadOnly ? 'none' : '#0036ff21',
          cursor: isReadOnly ? 'default' : 'text',
          'scrollbar-width': 'none',
          'padding-top': 0,
        }
      }
      if (
        widget instanceof Annotations.CheckButtonWidgetAnnotation ||
        widget instanceof Annotations.RadioButtonWidgetAnnotation
      ) {
        return {
          ...commonStyle,
          'background-color': isReadOnly ? 'white' : '#dee4ff',
          cursor: isReadOnly ? 'default' : 'pointer',
          'border-radius': 0,
        }
      }
    }

    UI.setHeaderItems((header) => {
      const defaultHeader = header.getHeader('default')
      defaultHeader.push({
        img: 'icon-header-page-manipulation-page-rotation-counterclockwise-line',
        index: -1,
        type: 'actionButton',
        dataElement: 'rotateLeftButton',
        title: 'Rotation gauche',
        onClick: () => documentViewer.rotateCounterClockwise(),
      })
      defaultHeader.push({
        img: 'icon-header-page-manipulation-page-rotation-clockwise-line',
        index: -1,
        type: 'actionButton',
        dataElement: 'rotateRightButton',
        title: 'Rotation droite',
        onClick: () => documentViewer.rotateClockwise(),
      })
      defaultHeader.push({
        img: 'icon-header-full-screen',
        index: -1,
        type: 'actionButton',
        dataElement: 'fullScreenButton',
        title: 'Plein écran',
        onClick: () => UI.toggleFullScreen(),
      })
    })

    documentViewer.addEventListener('annotationsLoaded', () => setAnnotationsLoaded(true))

    annotationManager.addEventListener('fieldChanged', (field: PdfjsField, value: string) =>
      document.body.dispatchEvent(
        new CustomEvent(CUSTOM_EVENT_FIELD_CHANGED, {
          detail: { field, value },
        }),
      ),
    )

    documentViewer.addEventListener('click', (event: MouseEvent) => {
      const [annot] = annotationManager.getAnnotationsByMouseEvent(event)
      if (annot instanceof Annotations.TextWidgetAnnotation) {
        document.body.dispatchEvent(
          new CustomEvent(CUSTOM_EVENT_TEXT_WIDGET_FOCUS, {
            detail: { name: annot.fieldName },
          }),
        )
      }
    })

    documentViewer.addEventListener('dblClick', (event: MouseEvent) => {
      const [annot] = annotationManager.getAnnotationsByMouseEvent(event)
      if (annot instanceof Annotations.TextWidgetAnnotation) {
        document.body.dispatchEvent(new CustomEvent(CUSTOM_EVENT_TEXT_WIDGET_DBCLICK))
      }
    })

    documentViewer.addEventListener('pageComplete', (pageNumber: number) => {
      const annotations = annotationManager
        .getAnnotationsList()
        .filter((annotation) => annotation.PageNumber === pageNumber)

      if (annotations.length > 0) {
        annotationManager.drawAnnotationsFromList(annotations)
      }
    })
  }

  // Application du mode lecture seule sur les champs puis redraw des annotations
  useEffect(() => {
    if (webViewerInstance && isAnnotationsLoaded) {
      const { annotationManager, Annotations } = webViewerInstance.Core

      if (readOnly) {
        annotationManager.enableReadOnlyMode()
      } else {
        annotationManager.disableReadOnlyMode()
      }

      const annotations = annotationManager.getAnnotationsList()
      annotations.forEach((annot) => {
        if (annot instanceof Annotations.TextWidgetAnnotation) {
          annot.fieldFlags.set('ReadOnly', readOnly)
        }
      })
      annotationManager.drawAnnotationsFromList(annotations)
    }
  }, [webViewerInstance, isAnnotationsLoaded, readOnly])

  // Une seule instance de WebViewer montée, on recharge le document si la source change
  useEffect(() => {
    if (webViewerInstance) {
      webViewerInstance.UI.loadDocument(getFileUrl(), {
        filename: fileName,
        extension: 'pdf',
      })
    }
  }, [fileContent, fileName, fileUrl, getFileUrl, webViewerInstance])

  const getTerminalFields = useCallback((fields: PdfjsField[]): PdfjsField[] => {
    return fields.reduce<PdfjsField[]>((acc, field) => {
      if (field.isTerminal()) {
        return [...acc, field]
      }
      return [...acc, ...getTerminalFields(field.children)]
    }, [])
  }, [])

  // Injection des valeurs dans les champs, listener fieldChanged désactivé le temps du remplissage
  useEffect(() => {
    if (webViewerInstance && isAnnotationsLoaded) {
      const rootFields: PdfjsField[] = webViewerInstance.Core.annotationManager
        .getFieldManager()
        .getFields()

      document.body.removeEventListener(
        CUSTOM_EVENT_FIELD_CHANGED,
        handleFieldChanged as EventListener,
      )

      fieldValues?.forEach((fieldValue) => {
        const match = fieldValue.name?.match(/^#PARTIAL#(#.*#)$/)

        getTerminalFields(rootFields).forEach((field) => {
          if (match && field.name.match(`${match[1]}$`)) {
            field.setValue(fieldValue.value)
          } else if (fieldValue.name === field.name) {
            field.setValue(fieldValue.value)
          }
        })
      })

      document.body.addEventListener(
        CUSTOM_EVENT_FIELD_CHANGED,
        handleFieldChanged as EventListener,
      )
    }
  }, [isAnnotationsLoaded, fieldValues, webViewerInstance, getTerminalFields, handleFieldChanged])

  useEffect(() => {
    if (handleExitFullScreen) {
      document.addEventListener('fullscreenchange', handleExitFullScreen)

      return () => document.removeEventListener('fullscreenchange', handleExitFullScreen)
    }
  }, [handleExitFullScreen])

  // UI Legacy pour conserver le comportement v8 sur les widgets de formulaire
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      WebViewer(
        {
          licenseKey: PDFJS_EXPRESS_KEY,
          path: PDFJS_EXPRESS_PATH || '/webviewer',
          ui: 'legacy',
          disabledElements: [
            'toolbarGroup-View',
            'toolbarGroup-Annotate',
            'toolbarGroup-Shapes',
            'toolbarGroup-Insert',
            'toolbarGroup-Edit',
            'toolbarGroup-FillAndSign',
            'toolbarGroup-Forms',
            'toggleNotesButton',
            'menuButton',
            'selectToolButton',
            'panToolButton',
            'viewControlsButton',
            'outlinesPanelButton',
            'toolsHeader',
            'ribbons',
            'annotationPopup',
            'toolsOverlay',
            'leftPanel-thumbnailControl',
            'toolbarGroup-Insert',
            'pageManipulationButton',
            'pageManipulationOverlay',
            'insertPageButton',
            'replacePageButton',
            'thumbnailControlMenu',
            'thumbnailControl',
          ],
        },
        viewer.current!,
      ).then((instance: any) => {
        onInitWebViewer(instance)
        setWebViewerInstance(instance)
      })
    }
  }, [])

  useEffect(() => {
    return () => {
      if (webViewerInstance) {
        try {
          webViewerInstance.Core.documentViewer.closeDocument()
          webViewerInstance.UI.dispose()
        } catch (_) {
          // noop
        }
      }
    }
  }, [webViewerInstance])

  return <div className={classNames(styles.pdfViewer)} ref={viewer}></div>
}
